import React, { useRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * SuiteSubnav — Sottomenu orizzontale contestuale.
 *
 * Master Shell v2 · v1.11.0 (28/07/2026 · Brand Book v1.0)
 * ========================================================
 * Ancorato sotto la TopBar per moduli con sub-navigazione
 * (Fatturazione, Clienti, Cruscotto Pratiche). Scorrevole
 * orizzontalmente su viewport ridotti (tablet/notebook) senza
 * far scrollare la pagina intera.
 *
 * Props:
 *   - items: [{ value, label, testid?, icon? }]
 *   - activeValue
 *   - onSelect(value)
 */
export function SuiteSubnav({ items = [], activeValue, onSelect, className, testid = "suite-subnav" }) {
  const scrollRef = useRef(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setOverflow(el.scrollWidth > el.clientWidth + 2);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items]);

  if (!items.length) return null;

  return (
    <div
      className={cn("suite-subnav w-full shrink-0", className)}
      data-testid={testid}
      style={{
        height: 42,
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E5E7EB",
        position: "relative",
      }}
    >
      <div
        ref={scrollRef}
        className="flex items-stretch h-full"
        style={{
          gap: 4,
          padding: "0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          scrollbarWidth: "thin",
        }}
      >
        {items.map((it) => {
          const active = activeValue === it.value;
          const Icon = it.icon;
          return (
            <button
              key={it.value}
              type="button"
              onClick={() => onSelect?.(it.value)}
              data-testid={it.testid || `subnav-${it.value}`}
              data-active={active ? "true" : "false"}
              className="suite-subnav-item"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0 14px",
                height: "100%",
                border: "none",
                background: "transparent",
                color: active ? "var(--gis-green)" : "#6B7280",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
                position: "relative",
                fontFamily: "var(--gis-font-heading)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                letterSpacing: "-0.005em",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.color = "var(--gis-green)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.color = "#6B7280";
              }}
            >
              {Icon && <Icon size={14} strokeWidth={1.75} />}
              {it.label}
              {active && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: Icon ? 26 : 10,
                    right: 10,
                    bottom: 0,
                    height: 2,
                    backgroundColor: "var(--gis-green)",
                    borderRadius: "2px 2px 0 0",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
      {overflow && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 24,
            pointerEvents: "none",
            background: "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.95))",
          }}
        />
      )}
    </div>
  );
}

export default SuiteSubnav;

